import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { logoFor } from "@/lib/companies";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicShare() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, meta: null, error: null });
  const [fileUrl, setFileUrl] = useState(null);

  useEffect(() => {
    let revoke = null;
    async function load() {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/share/${token}`);
        setState({ loading: false, meta: data, error: null });
        const fileRes = await axios.get(`${BACKEND_URL}/api/public/share/${token}/file`, { responseType: "blob" });
        const url = URL.createObjectURL(fileRes.data);
        revoke = url;
        setFileUrl(url);
      } catch (e) {
        const status = e.response?.status;
        let msg = e.response?.data?.detail || "This link is no longer available.";
        if (status === 410) msg = "This share link has expired.";
        else if (status === 404) msg = "This share link is invalid or the file was removed.";
        setState({ loading: false, meta: null, error: msg });
      }
    }
    load();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [token]);

  const { loading, meta, error } = state;
  const isImage = meta?.content_type?.startsWith("image/");
  const isPdf = meta?.content_type === "application/pdf";
  const logo = meta?.company ? logoFor(meta.company) : null;

  return (
    <div className="min-h-screen bg-sand paper-grain">
      <header className="border-b border-border/70 bg-card/60">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="label-caps">Lowcountry Brands</div>
            <div className="font-heading text-lg mt-1">Studio Ledger</div>
          </div>
          {logo && <img src={logo} alt={meta.company} className="h-9 w-auto object-contain" />}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading && (
          <div className="text-center py-24 text-muted-foreground">Loading…</div>
        )}

        {!loading && error && (
          <div className="card-elev p-10 text-center">
            <div className="label-caps mb-2 text-terracotta">Unavailable</div>
            <h1 className="font-heading text-4xl">Sorry, this link can't be opened.</h1>
            <p className="mt-4 text-muted-foreground">{error}</p>
          </div>
        )}

        {!loading && meta && (
          <div className="space-y-8 animate-fade-up">
            <div>
              <div className="label-caps">{meta.item_type || "Send-off"}</div>
              <h1 className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight leading-[1.05]">
                {meta.title}
              </h1>
              <div className="mt-3 text-sm text-muted-foreground">
                {meta.customer?.name && <span>For {meta.customer.name}{meta.customer.company ? ` · ${meta.customer.company}` : ""} · </span>}
                Expires {new Date(meta.expires_at).toLocaleString()}
              </div>
            </div>

            <div className="card-elev overflow-hidden bg-card">
              {isImage && fileUrl && (
                <img src={fileUrl} alt={meta.title} className="w-full h-auto max-h-[70vh] object-contain bg-muted/30" />
              )}
              {isPdf && fileUrl && (
                <iframe src={fileUrl} title={meta.title} className="w-full h-[80vh] bg-muted/30" />
              )}
              {!isImage && !isPdf && (
                <div className="p-10 text-center text-muted-foreground">
                  This file type can't be previewed. Use the download link below.
                </div>
              )}
              {fileUrl && (
                <div className="border-t border-border/60 px-5 py-3 flex items-center justify-between text-sm">
                  <span className="truncate text-muted-foreground">{meta.original_filename || "Attachment"}</span>
                  <a
                    href={fileUrl}
                    download={meta.original_filename || "file"}
                    className="inline-flex items-center gap-2 border border-espresso px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-espresso hover:bg-espresso hover:text-sand"
                  >
                    Download
                  </a>
                </div>
              )}
            </div>

            <div className="text-center text-xs text-muted-foreground">
              Sent by Lowcountry Brands · This link expires automatically
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
